import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useSessionStore } from '../store/SessionStore';
import i18n from '../i18n/i18n';

// MUI imports
import { Box, Typography } from '@mui/material';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RemoveIcon from '@mui/icons-material/Remove';

// Types
import { SessionData } from '../types/sessionData';

// Helpers
import { truncateText } from '../helpers/functions/truncateText';

interface SessionProps {
    session: SessionData;
    nbSessions: number;
    customStyle: boolean;
}

const Session = ({ session }: SessionProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { setSession } = useSessionStore();
    const borderColor = theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)';

    const handleSessionClick = () => {
        setSession(session);
        navigate(`/sessions/${session.id}`);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                margin: '0 auto',
                width: '92%',
                height: '90px',
                padding: '10px 0',
                backgroundColor: theme.palette.background.default,
                borderTop: `0.1em ${borderColor} solid`
            }}
            onClick={handleSessionClick}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', width: '70%' }}>
                <Typography
                    sx={{
                        display: 'flex',
                        minHeight: '2em',
                        color: theme.palette.secondary.main,
                        alignContent: 'center',
                        alignItems: 'center',
                        fontSize: '16px',
                        fontWeight: 'bold'
                    }}
                >
                    {/* Truncate the training label to a maximum of 25 characters for display */}
                    {session.closingDate ? <CheckCircleOutlineIcon sx={{ color: theme.palette.success.main, marginRight: '3px' }} /> : null}
                    {truncateText(session.trainingLabel, 25)}
                </Typography>
                <Box>
                    <Typography
                        sx={{
                            color: theme.palette.text.primary,
                            fontSize: '14.5px'
                        }}
                    >
                        {/* Truncate the session name to a maximum of 25 characters for display */}
                        {truncateText(session.sessionName, 25)}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <Typography sx={{ color: theme.palette.text.primary, fontSize: '14.5px' }}>{session.sessionBegin}</Typography>
                        <ArrowRightAltIcon sx={{ fontSize: '20px', paddingTop: '1px', margin: '0 5px', color: theme.palette.text.custom }} />
                        <Typography sx={{ color: theme.palette.text.primary, fontSize: '14.5px' }}>{session.sessionEnd}</Typography>
                        <RemoveIcon sx={{ fontSize: '20px', fontWeight: '700', paddingTop: '1px', margin: '0 5px', color: theme.palette.text.custom }} />
                        <Typography sx={{ color: theme.palette.text.primary, fontSize: '14.5px' }}>
                            {session.nomberDays}
                            {i18n.t('Sessions.info.days')}
                        </Typography>
                    </Box>
                </Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    width: '30%',
                    height: '100%',
                    color: theme.palette.text.custom
                }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                    <PeopleAltOutlinedIcon sx={{ fontSize: '1.75em' }} />
                    <Typography sx={{ fontSize: '1.25em', fontWeight: '700', marginLeft: '5px' }}>{Number(session.trainees.length)}</Typography>
                </Box>
                <Typography sx={{ fontWeight: '700', fontSize: '1.25em' }}>{session.sessionToken}</Typography>
            </Box>
        </Box>
    );
};

export default Session;
